import { defineStore } from 'pinia'
import { ref } from 'vue'

const DEFAULT_MAX_ITEMS = 5
const MIN_MAX_ITEMS = 3
const MAX_MAX_ITEMS = 6

/**
 * Synthetic item id for the always-pinned "Home / Accueil" entry. Held in the
 * store + source so it's the first slot on every device and cannot be removed
 * by the user from the customization UI.
 */
export const BOTTOM_NAV_HOME_ID = 'home'

export const useBottomNavStore = defineStore(
  'bottomNav',
  () => {
    const enabledItemIds = ref<string[]>([])
    const maxItems = ref<number>(DEFAULT_MAX_ITEMS)
    const hasUserCustomized = ref<boolean>(false)
    const enabled = ref<boolean>(false)

    const toggleItem = (id: string) => {
      // Home is non-removable; ignore attempts to toggle it off.
      if (id === BOTTOM_NAV_HOME_ID) return
      const idx = enabledItemIds.value.indexOf(id)
      if (idx >= 0) {
        enabledItemIds.value.splice(idx, 1)
      } else {
        enabledItemIds.value.push(id)
      }
      hasUserCustomized.value = true
    }

    const reorder = (fromIdx: number, toIdx: number) => {
      if (fromIdx === toIdx) return
      const list = [...enabledItemIds.value]
      const [moved] = list.splice(fromIdx, 1)
      if (!moved) return
      list.splice(toIdx, 0, moved)
      enabledItemIds.value = list
      hasUserCustomized.value = true
    }

    const setOrder = (ids: string[]) => {
      enabledItemIds.value = [...ids]
      hasUserCustomized.value = true
    }

    const setMaxItems = (n: number) => {
      maxItems.value = Math.max(MIN_MAX_ITEMS, Math.min(MAX_MAX_ITEMS, Math.round(n)))
    }

    const resetToDefaults = () => {
      enabledItemIds.value = []
      maxItems.value = DEFAULT_MAX_ITEMS
      hasUserCustomized.value = false
    }

    const pruneMissing = (validIds: Set<string>) => {
      // Always preserve home — it's a synthetic item that may not appear in the
      // dynamic CRUD-derived id set.
      const next = enabledItemIds.value.filter((id) => id === BOTTOM_NAV_HOME_ID || validIds.has(id))
      if (next.length !== enabledItemIds.value.length) {
        enabledItemIds.value = next
      }
    }

    return {
      enabledItemIds,
      maxItems,
      hasUserCustomized,
      enabled,
      toggleItem,
      reorder,
      setOrder,
      setMaxItems,
      resetToDefaults,
      pruneMissing,
    }
  },
  { persist: true },
)

export const BOTTOM_NAV_MAX_ITEMS_RANGE = { min: MIN_MAX_ITEMS, max: MAX_MAX_ITEMS } as const
